import React, { useEffect, useMemo, useState } from "react";
import { Container, Form, InputGroup, Button, Row, Col, Card } from "react-bootstrap";
import { useLang } from "../../contexts/LanguageContext";
import apiUrls from "../../api/apiUrls";
import fetchWithAuth from "../../api/fetchWithAuth";
import { languageMeta } from "../../translations";

import "./SearchMain.css";
import Book from "../Book";

const SearchMain = () => {
  const { t } = useLang();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const trimmedQuery = useMemo(() => query.trim(), [query]);
  const isQueryReady = trimmedQuery.length >= 4;

  useEffect(() => {
    if (!isQueryReady) {
      setResults([]);
      setError(null);
      return;
    }

    const controller = new AbortController();
    const debounceId = setTimeout(async () => {
      try {
        setIsLoading(true);
        const endpoint = apiUrls.searchBooks(trimmedQuery, { limit: 12 });

        const response = await fetchWithAuth(endpoint, {
          signal: controller.signal,
        });

        if (!response.ok) {
          const raw = await response.text();
          let data = null;
          try {
            data = raw ? JSON.parse(raw) : null;
          } catch {
            // ignore malformed payloads
          }
          const backendMessage = data?.error || data?.message;
          const translatedMessage =
            backendMessage && t(backendMessage) !== backendMessage
              ? t(backendMessage)
              : t("SEARCH_GENERIC_ERROR");

          throw new Error(translatedMessage);
        }

        const data = await response.json();
        setResults(Array.isArray(data?.results) ? data.results : []);
        setError(null);
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }
        setError(err.message || t("SEARCH_GENERIC_ERROR"));
      } finally {
        setIsLoading(false);
      }
    }, 350);

    return () => {
      clearTimeout(debounceId);
      controller.abort();
    };
  }, [isQueryReady, trimmedQuery]);

  const handleSubmit = (event) => {
    event.preventDefault();
  };

  return (
    <Container className="py-2">
      <div className="search-panel">
        <header className="mb-5 text-center text-md-start">
          <h1 className="mb-3">{t("SEARCH_TITLE")}</h1>
          <p className="mb-4">{t("SEARCH_SUBTITLE")}</p>
          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Form.Control
                type="search"
                placeholder={t("SEARCH_PLACEHOLDER")}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label={t("SEARCH_PLACEHOLDER")}
              />
              <Button type="submit" variant="primary">
                {t("SEARCH_BUTTON")}
              </Button>
            </InputGroup>
          </Form>
        </header>

        <section>
          {!isQueryReady ? (
            <p className="text-normal">{t("SEARCH_MIN_CHARS_HINT")}</p>
          ) : isLoading ? (
            <p className="text-normal">{t("LOGIN_LOADING")}</p>
          ) : error ? (
            <p className="text-danger mb-0">{error}</p>
          ) : results.length === 0 ? (
            <div className="text-center py-5">
              <h3 className="h5">{t("SEARCH_RESULTS_EMPTY", { QUERY: query })}</h3>
              <p className="mb-0">{t("SEARCH_RESULTS_SUGGESTION")}</p>
            </div>
          ) : (
            <>
              <div className="text-center text-md-start mb-4">
                <p className="mb-0 fw-semibold">
                  {t("SEARCH_RESULTS_COUNT_LABEL", { COUNT: results.length })}
                </p>
              </div>
              <Row className="g-4">
                {results.map((book) => {
                  const id = book.book_id ?? book.id;
                  const title = book.book_title ?? book.title ?? t("SEARCH_RESULT_UNTITLED");
                  const author = book.book_author ?? book.author ?? "";
                  const languageCode = book.book_language ?? book.language;
                  const category = book.book_category ?? book.category ?? t("SEARCH_RESULT_CATEGORY_UNKNOWN");
                  const languageLabel =
                    (languageCode && languageMeta[languageCode]?.label) ||
                    languageCode?.toUpperCase() ||
                    t("SEARCH_RESULT_LANGUAGE_UNKNOWN");
                  const snippet = book.snippet ?? book.book_resume ?? "";

                  return (
                    <Col key={id || title} md={6} lg={4}>
                      
                      <Card className="h-100 shadow-sm">
                        <Card.Body className="d-flex flex-column">
                          <div className="cover-link">
                            <Book book={book}></Book>
                          </div>                          
                          <Card.Title>{title}</Card.Title>
                          {author && (
                            <Card.Subtitle className="mb-2 text-muted">{author}</Card.Subtitle>
                          )}
                          {snippet && (
                            <Card.Text className="flex-grow-1">{snippet}</Card.Text>
                          )}
                          <div className="small mb-3">
                            <div>
                              {t("SEARCH_RESULT_LANGUAGE")}: {languageLabel}
                            </div>
                            <div>
                              {t("SEARCH_RESULT_CATEGORY")}: {category}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </>
          )}
        </section>
      </div>
    </Container>
  );
};

export default SearchMain;
